// Pure variant logic for the Payment History "payment notice" shown on the
// Operations → Approvals page (see app-operations-approvals.tsx,
// data-testid="notice-payment-pending"). The page maps the server's definitive
// payment-status outcome plus a client-side 60s poll-timeout flag onto one of
// four notice variants. That mapping is extracted here as a pure function so it
// can be unit-tested without spinning up the real 60s timer (Task #2553).

// Outcomes returned by GET /api/app/operations/approvals/:id/payment-status
// (server/routes/operations.ts).
export type PaymentStatusOutcome =
  | "recorded"
  | "no_charge_needed"
  | "failed_to_record"
  | "processing";

export type PaymentNoticeKind = "failed" | "no_charge" | "timed-out" | "pending";

export interface PaymentNoticeVariant {
  kind: PaymentNoticeKind;
  /** The notice body copy shown to the host. */
  message: string;
  /** Whether the inline Retry button renders (only the failed variant). */
  showRetry: boolean;
  /** Whether the "contact support with reference TASK-<id>" line renders. */
  showReference: boolean;
}

const MESSAGES: Record<PaymentNoticeKind, string> = {
  failed:
    "This charge failed to record. The task was approved, but the payment was never created — your card has not been charged for it.",
  no_charge:
    "This task didn't include a vendor charge, so there's no payment to record here.",
  "timed-out":
    "This is taking longer than usual. The charge for this task hasn't been recorded yet — try reloading the page, and if it still doesn't appear, contact support.",
  pending:
    "Payment for this task is still being recorded — check back shortly. The matching row will appear and highlight automatically once the charge is recorded.",
};

/**
 * Resolve which payment-notice variant to render from the server outcome and the
 * client-side poll-timeout flag. The server's definitive outcome wins; while the
 * status check is still loading (`outcome` undefined) we fall back to the
 * legacy timeout-driven states.
 */
export function resolvePaymentNotice(
  outcome: PaymentStatusOutcome | undefined,
  txPollTimedOut: boolean,
): PaymentNoticeVariant {
  const kind: PaymentNoticeKind =
    outcome === "failed_to_record"
      ? "failed"
      : outcome === "no_charge_needed"
        ? "no_charge"
        : txPollTimedOut
          ? "timed-out"
          : "pending";

  return {
    kind,
    message: MESSAGES[kind],
    showRetry: kind === "failed",
    showReference: kind === "failed",
  };
}
